import {
  IconMessage,
  IconPhoto,
  IconUser,
  IconTemplate,
} from "@tabler/icons-react";
import type { ElementType } from "react";

export interface Parameter {
  name: string;
  type: string;
  description: string;
  values?: string[];
}

export interface ApiEndpoint {
  title: string;
  method: string;
  endpoint: string;
  description: string;
  icon: ElementType;
  payload: Record<string, unknown>;
  curlExample: string;
  headers: Record<string, string>;
  parameters?: Parameter[];
}

const apiKey = "YOUR_API_KEY";
const baseUrl = "https://meta.superagents.com.br/webhook";
const sendMessageEndpoint = `${baseUrl}/sendMessage`;
const sendMediaEndpoint = `${baseUrl}/sendMedia`;
const callHumanEndpoint = `${baseUrl}/callHuman`;
const sendTemplateEndpoint = `${baseUrl}/sendTemplate`;

// Shared headers
const commonHeaders = {
  "x-api-key": apiKey,
};

const jsonHeaders = {
  "Content-Type": "application/json",
  ...commonHeaders,
};

const multipartHeaders = {
  "Content-Type": "multipart/form-data",
  ...commonHeaders,
};

// Common Parameters (DRY)
const paramFrom: Parameter = {
  name: "from",
  type: "String",
  description: "Número de telefone da instância (formato internacional)",
};

const paramTo: Parameter = {
  name: "to",
  type: "String",
  description:
    "Número de telefone do destinatário (formato internacional). Opcional quando 'recipient' é informado; se ambos vierem, 'to' (telefone) prevalece",
};

const paramRecipient: Parameter = {
  name: "recipient",
  type: "String",
  description:
    "BSUID do destinatário sem telefone ('BR.<id>'). Opcional. Alternativa a 'to' para contatos BSUID-only",
};

const paramMessage: Parameter = {
  name: "message",
  type: "String",
  description: "Conteúdo da mensagem de texto a ser enviada",
};

// Formatos e tetos espelham a validação do backend (`MEDIA_KIND_BY_MIME` e
// `META_MAX_BYTES_BY_KIND`): os limites são os documentados pela Meta, e o
// teto de 10 MB vale para número que ainda envia pelo caminho antigo (SA-1384).
const paramMedia: Parameter = {
  name: "media",
  type: "File/URL",
  description:
    "Arquivo de mídia a ser enviado ou URL pública do arquivo. Formatos aceitos e limite por tipo: imagem JPEG ou PNG (até 5 MB); vídeo MP4 ou 3GP (até 16 MB); áudio MP3, OGG, M4A, AAC ou AMR (até 16 MB); documento PDF, Word (DOC, DOCX), Excel (XLS, XLSX), PowerPoint (PPT, PPTX) ou texto (TXT) (até 100 MB). Números que ainda não foram migrados para o novo envio de mídia aceitam arquivos de até 10 MB, em qualquer tipo.",
};

const paramData: Parameter = {
  name: "data",
  type: "Object",
  description:
    "Objeto com a mensagem: 'text_content' é a legenda (opcional). O tipo da mensagem é definido pelo formato do arquivo enviado em 'media': o 'message_content_type' informado aqui é ignorado.",
};

const paramEscalationReason: Parameter = {
  name: "escalation_reason",
  type: "String",
  description:
    '"ai" quando a IA decide transferir para humano, "client" quando o cliente solicita falar com humano',
  values: ["ai", "client"],
};

const paramEscalationReasonDetails: Parameter = {
  name: "escalation_reason_details",
  type: "String",
  description: "Descrição detalhada do motivo da escalação",
};

const paramTemplateId: Parameter = {
  name: "templateId",
  type: "String",
  description: "ID do template configurado no WhatsApp Business",
};

const paramSendToWA: Parameter = {
  name: "sendToWA",
  type: "Boolean",
  description:
    "true para enviar o template ao WhatsApp; false quando o template já foi disparado externamente e você quer apenas registrar na plataforma para aparecer na interface",
};

const paramInboxType: Parameter = {
  name: "inboxType",
  type: "String",
  description: "Define o tipo de atendimento",
  values: ["ai", "human"],
};

const paramComponents: Parameter = {
  name: "components",
  type: "Array",
  description: "Parâmetros do template seguindo estrutura da API Meta",
};

const generateCurlExampleJSON = ({
  method,
  endpoint,
  headers,
  payload,
}: {
  method: string;
  endpoint: string;
  headers: Record<string, string>;
  payload: unknown;
}): string => {
  const headerLines = Object.entries(headers)
    .filter(
      ([name, value]) =>
        !(
          name.toLowerCase() === "content-type" &&
          value.toLowerCase().includes("multipart/form-data")
        )
    )
    .map(([name, value]) => '  -H "' + name + ": " + value + '" \\\\');

  const body = JSON.stringify(payload, null, 2);

  const lines = [
    `curl -X ${method} ${endpoint} \\\\`,
    ...headerLines,
    `  -d '${body}'`,
  ];

  return lines.join("\n");
};

// Builder helpers to keep endpoint definitions DRY
const buildJsonEndpoint = <P extends Record<string, unknown>>({
  title,
  method,
  endpoint,
  description,
  icon,
  parameters,
  payload,
}: {
  title: string;
  method: string;
  endpoint: string;
  description: string;
  icon: ElementType;
  parameters: Parameter[];
  payload: P;
}): ApiEndpoint => ({
  title,
  method,
  endpoint,
  description,
  icon,
  headers: jsonHeaders,
  parameters,
  payload,
  curlExample: generateCurlExampleJSON({
    method,
    endpoint,
    headers: jsonHeaders,
    payload,
  }),
});

const generateCurlExampleMultipart = ({
  method,
  endpoint,
  headers,
  formFields,
}: {
  method: string;
  endpoint: string;
  headers: Record<string, string>;
  formFields: Array<{ key: string; value: string }>;
}): string => {
  const headerLines = Object.entries(headers)
    .filter(
      ([name, value]) =>
        !(
          name.toLowerCase() === "content-type" &&
          value.toLowerCase().includes("multipart/form-data")
        )
    )
    .map(([name, value]) => '  -H "' + name + ": " + value + '" \\\\');

  const formLines = formFields.map(
    ({ key, value }) => '  -F "' + key + "=" + value + '" \\\\'
  );

  const lines = [
    `curl -X ${method} ${endpoint} \\\\`,
    ...headerLines,
    ...formLines,
  ];

  // Remove trailing line continuation from the last form line
  if (lines.length > 0) {
    const lastIdx = lines.length - 1;
    lines[lastIdx] = lines[lastIdx].replace(/ \\\\\\$/, "");
  }

  return lines.join("\n");
};

const buildMultipartEndpoint = ({
  title,
  method,
  endpoint,
  description,
  icon,
  parameters,
  payload,
  formFields,
}: {
  title: string;
  method: string;
  endpoint: string;
  description: string;
  icon: ElementType;
  parameters: Parameter[];
  payload: Record<string, unknown>;
  formFields: Array<{ key: string; value: string }>;
}): ApiEndpoint => ({
  title,
  method,
  endpoint,
  description,
  icon,
  headers: multipartHeaders,
  parameters,
  payload,
  curlExample: generateCurlExampleMultipart({
    method,
    endpoint,
    headers: multipartHeaders,
    formFields,
  }),
});

const sendMessagePayload = {
  from: "5516988888888",
  to: "5516999999999",
  message: "Olá! Esta é uma mensagem enviada via API.",
};

const callHumanPayload = {
  from: "5516988888888",
  to: "5516999999999",
  escalation_reason: "client",
  escalation_reason_details:
    "Cliente solicitou falar com um humano por preferência pessoal",
};

const templatePayload = {
  from: "5516988888888",
  to: "5516999999999",
  data: {
    templateId: "1388102478962632",
    sendToWA: true,
    inboxType: "ai",
    components: [
      {
        type: "header",
        parameters: [
          {
            type: "text",
            parameter_name: "nome_cliente",
            text: "João Silva",
          },
        ],
      },
      {
        type: "body",
        parameters: [
          {
            type: "text",
            parameter_name: "numero_meses",
            text: "3",
          },
        ],
      },
    ],
  },
};

export const apiEndpoints: ApiEndpoint[] = [
  buildJsonEndpoint({
    title: "Send Message",
    method: "POST",
    endpoint: sendMessageEndpoint,
    description:
      "Envia uma mensagem de texto para o WhatsApp através da nossa API.",
    icon: IconMessage,
    parameters: [paramFrom, paramTo, paramRecipient, paramMessage],
    payload: sendMessagePayload,
  }),
  buildMultipartEndpoint({
    title: "Send Media",
    method: "POST",
    endpoint: sendMediaEndpoint,
    description:
      "Envia mídia (imagem, vídeo, áudio ou documento) com texto opcional para o WhatsApp. Aceita tanto arquivo físico quanto URL pública do arquivo. O tipo da mensagem é definido pelo formato do arquivo; os formatos aceitos e o limite de tamanho de cada tipo estão no parâmetro 'media'.",
    icon: IconPhoto,
    parameters: [paramFrom, paramTo, paramRecipient, paramMedia, paramData],
    payload: {
      from: "5516988888888",
      to: "5516999999999",
      media: "@file.jpg",
      data: {
        message: {
          text_content: "Aqui está a imagem que você solicitou!",
          message_content_type: "image",
        },
      },
    },
    formFields: [
      { key: "from", value: "5516988888888" },
      { key: "to", value: "5516999999999" },
      { key: "media", value: "@/path/to/your/file.jpg" },
      {
        key: "data",
        value: JSON.stringify({
          message: {
            text_content: "Aqui está a imagem!",
            message_content_type: "image",
          },
        }),
      },
    ],
  }),
  buildJsonEndpoint({
    title: "Send Template",
    method: "POST",
    endpoint: sendTemplateEndpoint,
    description:
      "Envia um template do WhatsApp Business configurado com parâmetros dinâmicos. O campo 'components' segue exatamente a estrutura da API oficial da Meta para templates do WhatsApp Business.",
    icon: IconTemplate,
    parameters: [
      paramTemplateId,
      paramTo,
      paramRecipient,
      paramSendToWA,
      paramInboxType,
      paramComponents,
    ],
    payload: templatePayload,
  }),
  buildJsonEndpoint({
    title: "Call Human",
    method: "POST",
    endpoint: callHumanEndpoint,
    description:
      'Escalona a conversa para um atendente humano com motivo especificado. Após a escalação, o chat fica com status "Aguardando atendimento" até que um atendente humano inicie o atendimento, momento em que o chat é atribuído para aquele atendente.',
    icon: IconUser,
    parameters: [
      paramFrom,
      paramTo,
      paramRecipient,
      paramEscalationReason,
      paramEscalationReasonDetails,
    ],
    payload: callHumanPayload,
  }),
];

export type { ApiEndpoint as EndpointCardProps };
